/**
 * HubList — the unified per-doc lifecycle table inside the Hub.
 *
 * Replaces the legacy two-stack rendering (UploadRow + Documents)
 * at addin/src/variant-c/components/LibraryHub.tsx with one table
 * driven by ``DocumentRow[]`` from M2's ``mergeUploadsAndInventory``.
 *
 * Top: search input.
 * Below search: filter chips. Per Spec 34 §"Filter chip set
 *   extension" we add ``Limited`` and ``Failed`` to the handoff's
 *   four (All / Low conf / Unverified / Verified).
 * Body: ordered list of DocRow.
 *   Sort: in-flight + failed at the top (per Spec 34 §"Row sort"),
 *   ready/limited/verified below — preserves merger order within
 *   each cluster.
 * Footer: "+ Add files" button + counter ("N of M verified").
 *
 * Drag-drop: the entire pane is a drop target. ``onAddFiles`` fires
 * with a ``File[]`` regardless of how files arrived (drop event or
 * the explicit "+ Add files" picker). Per Spec 34 §"Open questions
 * — answered" #4 — drag-drop ships in v1.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import type { InventoryResolveEvent } from "../../../api/client";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import { useInventoryStream } from "../hooks/useInventoryStream";
import type { DocumentRow, ProcessingStatus } from "../types";
import { DocRow } from "./DocRow";
import { InventoryTimeline } from "./InventoryTimeline";
import { LiveDocRow } from "./LiveDocRow";
import { StreamingStatusBar } from "./StreamingStatusBar";

export type HubListFilter = "all" | "low" | "unverified" | "verified" | "limited" | "failed";

export interface HubListProps {
  rows: DocumentRow[];
  /** Click on a row → opens HubDetail (M6). Optional during M5
   *  builds where the parent doesn't yet wire detail navigation. */
  onPickDoc?: (row: DocumentRow) => void;
  /** Drop or "+ Add files" → parent's upload flow. */
  onAddFiles: (files: File[]) => void;
  /** "Create artefact" from the "+" menu → parent opens the artefact builder.
   *  When absent, the "+" menu only offers "Upload a document". */
  onCreateArtefact?: () => void;
  /** Delete a document from the library. */
  onDeleteDoc?: (row: DocumentRow) => void;
  /** Download a document's original stored file. */
  onDownloadDoc?: (row: DocumentRow) => void;
  /** Refresh classification status for the current library. */
  onRefresh?: () => void;
  /** True while inventory is loading — shows progress instead of "No matches". */
  loading?: boolean;
  /** Current SSE progress label while loading. */
  loadingMessage?: string;
  /**
   * Latest raw SSE event from `streamLibraryInventory`.
   * When provided, replaces the generic loading banner with the
   * live streaming UX (StreamingStatusBar + InventoryTimeline).
   * Zero-touch to the SSE contract — this is the exact value from
   * VariantCShell's `libraryResolveEvent` state.
   */
  resolveEvent?: InventoryResolveEvent | null;
}

const TERMINAL_STATES: readonly ProcessingStatus[] = [
  "ready",
  "limited",
  "failed_upload",
  "failed_processing",
] as const;

function isTerminal(status: ProcessingStatus): boolean {
  return TERMINAL_STATES.includes(status);
}

/**
 * Sort key: 0 = top cluster (in-flight + failed — needs writer
 * attention), 1 = bottom cluster (ready / limited — working set).
 * Within each cluster, original merger order is preserved (stable
 * sort).
 */
function rowSortKey(row: DocumentRow): number {
  const s = row.processing_status;
  if (s === "uploading" || s === "indexing" || s === "classifying") {
    return 0;
  }
  if (s === "failed_upload" || s === "failed_processing") {
    return 0;
  }
  return 1;
}

function searchMatches(row: DocumentRow, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (row.filename.toLowerCase().includes(q)) return true;
  // Match against tag values — writers search by batch id /
  // substance / etc. without knowing the tag key by heart.
  const tags = row.source_classification?.tags ?? {};
  for (const value of Object.values(tags)) {
    if (typeof value === "string" && value.toLowerCase().includes(q)) {
      return true;
    }
  }
  // Match on doc_type too (free-text strategy classification).
  if (row.doc_type?.toLowerCase().includes(q)) return true;
  if (row.source_classification?.source_doctype?.toLowerCase().includes(q)) {
    return true;
  }
  return false;
}

function filterMatches(row: DocumentRow, filter: HubListFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "low":
      return !!row.low;
    case "unverified":
      return !row.verified;
    case "verified":
      return row.verified;
    case "limited":
      return row.processing_status === "limited";
    case "failed":
      return row.processing_status === "failed_upload" || row.processing_status === "failed_processing";
  }
}

interface FilterCounts {
  all: number;
  low: number;
  unverified: number;
  verified: number;
  limited: number;
  failed: number;
}

function computeCounts(rows: DocumentRow[]): FilterCounts {
  return {
    all: rows.length,
    low: rows.filter((r) => r.low).length,
    unverified: rows.filter((r) => !r.verified).length,
    verified: rows.filter((r) => r.verified).length,
    limited: rows.filter((r) => r.processing_status === "limited").length,
    failed: rows.filter((r) => r.processing_status === "failed_upload" || r.processing_status === "failed_processing")
      .length,
  };
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: surfaceColors.paper,
    position: "relative",
  },
  rootDrop: {
    // Brand-light overlay during a drag-over so the writer sees a
    // visible affordance. Doesn't block underlying clicks (the
    // overlay is a sibling, not a parent).
  },
  dropOverlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: "none",
    backgroundColor: brandColors.light,
    border: `2px dashed ${brandColors.primary}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 5,
    color: brandColors.primary,
    fontFamily: fonts.body,
    fontWeight: 700,
    fontSize: "13px",
    letterSpacing: "0.2px",
  },
  searchWrap: {
    padding: "8px 14px",
    backgroundColor: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  searchInner: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    padding: "5px 8px",
    marginBottom: "6px",
  },
  searchInput: {
    flex: 1,
    border: "none",
    backgroundColor: "transparent",
    outline: "none",
    fontSize: "11px",
    fontFamily: fonts.body,
    color: inkColors.ink,
  },
  filterRow: {
    display: "flex",
    columnGap: "4px",
    rowGap: "4px",
    flexWrap: "nowrap",
    overflowX: "auto",
    // Hide scrollbar on most browsers while keeping the row
    // scrollable for long filter chip sets.
    scrollbarWidth: "none",
    "::-webkit-scrollbar": { display: "none" },
  },
  filterChip: {
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    padding: "3px 8px",
    borderRadius: "9999px",
    backgroundColor: "transparent",
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.line}`,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    ":hover": {
      border: `1px solid ${brandColors.primary}`,
      color: inkColors.ink2,
    },
  },
  filterChipActive: {
    backgroundColor: inkColors.ink,
    color: "white",
    border: `1px solid ${inkColors.ink}`,
    ":hover": {
      backgroundColor: inkColors.ink,
      color: "white",
    },
  },
  filterDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  filterCount: {
    fontFamily: fonts.mono,
    fontSize: "8.5px",
    opacity: 0.7,
  },
  body: {
    flex: 1,
    overflow: "auto",
    backgroundColor: surfaceColors.paper,
  },
  empty: {
    padding: "30px",
    textAlign: "center",
    fontSize: "11px",
    color: inkColors.ink4,
    fontFamily: fonts.body,
  },
  // Empty-library CTA — shown when the library has NO documents yet
  // (distinct from "no filter matches" on a non-empty library).
  emptyLibrary: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "10px",
    padding: "40px 24px",
    textAlign: "center",
  },
  emptyLibraryTitle: {
    fontSize: "12px",
    fontWeight: 700,
    fontFamily: fonts.display,
    color: inkColors.ink,
  },
  emptyLibraryBody: {
    fontSize: "10.5px",
    fontFamily: fonts.body,
    color: inkColors.ink3,
    maxWidth: "240px",
    lineHeight: 1.5,
  },
  emptyLibraryBtn: {
    marginTop: "2px",
    fontSize: "11px",
    fontWeight: 700,
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    backgroundColor: brandColors.primary,
    color: "white",
    border: "none",
    borderRadius: "4px",
    padding: "8px 16px",
    cursor: "pointer",
    textTransform: "uppercase",
    ":hover": { opacity: 0.9 },
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 20px",
    rowGap: "10px",
  },
  loadingDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: brandColors.primary,
    opacity: 0.7,
  },
  loadingDotsRow: {
    display: "flex",
    columnGap: "4px",
    alignItems: "center",
  },
  loadingLabel: {
    fontSize: "10.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    textAlign: "center",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // Slim progress banner — rendered ABOVE the existing rows when a
  // re-resolve is in flight on a non-empty list. The big centered
  // ``loadingWrap`` is reserved for the empty/first-time case so the
  // writer doesn't lose row context during a re-resolve.
  loadingBanner: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    padding: "6px 14px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paperDim,
  },
  loadingBannerLabel: {
    fontSize: "10.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    minWidth: 0,
  },
  footer: {
    padding: "8px 14px",
    borderTop: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paperDim,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  addWrap: {
    position: "relative",
    display: "inline-flex",
  },
  addBtn: {
    fontSize: "10px",
    fontWeight: 700,
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    backgroundColor: "transparent",
    color: brandColors.primary,
    border: `1px solid ${brandColors.primary}`,
    borderRadius: "3px",
    padding: "4px 10px",
    cursor: "pointer",
    textTransform: "uppercase",
    ":hover": {
      backgroundColor: brandColors.light,
    },
  },
  // Invisible full-screen click-catcher so an outside click closes the menu.
  addMenuScrim: {
    position: "fixed",
    inset: 0,
    zIndex: 9,
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "default",
  },
  addMenu: {
    position: "absolute",
    bottom: "calc(100% + 4px)",
    left: 0,
    zIndex: 10,
    minWidth: "160px",
    backgroundColor: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    boxShadow: "0 8px 24px rgba(15, 10, 30, 0.18)",
    padding: "4px",
    display: "flex",
    flexDirection: "column",
  },
  addMenuItem: {
    textAlign: "left",
    fontSize: "11px",
    fontFamily: fonts.body,
    color: inkColors.ink,
    background: "transparent",
    border: "none",
    borderRadius: "3px",
    padding: "7px 9px",
    cursor: "pointer",
    ":hover": {
      backgroundColor: brandColors.light,
    },
  },
  refreshBtn: {
    fontSize: "10px",
    fontWeight: 700,
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    backgroundColor: "transparent",
    color: brandColors.primary,
    border: `1px solid ${brandColors.primary}`,
    borderRadius: "3px",
    padding: "4px 10px",
    cursor: "pointer",
    textTransform: "uppercase",
    ":hover": {
      backgroundColor: brandColors.light,
      color: brandColors.primary,
    },
  },
  hiddenInput: {
    display: "none",
  },
  footerCount: {
    flex: 1,
    textAlign: "right",
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
});

export const HubList: React.FC<HubListProps> = ({
  rows,
  onPickDoc,
  onAddFiles,
  onCreateArtefact,
  onDeleteDoc,
  onDownloadDoc,
  onRefresh,
  loading = false,
  loadingMessage = "Loading library…",
  resolveEvent = null,
}) => {
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<HubListFilter>("all");
  const [dragActive, setDragActive] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Streaming intelligence state — accumulates SSE event history and
  // derives phase / progress for the live UX components below.
  const stream = useInventoryStream(resolveEvent, loading);

  const counts = useMemo(() => computeCounts(rows), [rows]);

  const filtered = useMemo(() => {
    const matched = rows.filter((r) => filterMatches(r, filter) && searchMatches(r, query));
    // Stable sort: in-flight + failed first, ready/limited/etc.
    // after. Within a cluster, preserve merger input order.
    return matched
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const ka = rowSortKey(a.row);
        const kb = rowSortKey(b.row);
        if (ka !== kb) return ka - kb;
        return a.idx - b.idx;
      })
      .map((entry) => entry.row);
  }, [rows, filter, query]);

  // Drag-drop handlers — the whole pane is a drop target.
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) {
      setDragActive(true);
    }
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only deactivate when leaving the root, not its children.
    if (e.currentTarget === e.target) {
      setDragActive(false);
    }
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) {
        onAddFiles(files);
      }
    },
    [onAddFiles],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilePickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) onAddFiles(files);
      // Reset so picking the same file twice in a row still fires.
      e.target.value = "";
    },
    [onAddFiles],
  );

  return (
    <div
      className={styles.root}
      data-testid="hub-list"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragActive && (
        <div className={styles.dropOverlay} data-testid="hub-list-drop-overlay">
          Drop files to add
        </div>
      )}

      <div className={styles.searchWrap}>
        <div className={styles.searchInner}>
          <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
            <circle cx="5" cy="5" r="3.5" stroke={inkColors.ink4} strokeWidth="1.4" fill="none" />
            <path d="M8 8l3 3" stroke={inkColors.ink4} strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sources, tags, batch ids…"
            className={styles.searchInput}
            aria-label="Search sources"
          />
        </div>
        <div className={styles.filterRow} role="tablist" aria-label="Filter">
          {(
            [
              { id: "all", label: "All", n: counts.all },
              {
                id: "low",
                label: "Low conf",
                n: counts.low,
                dotColor: statusColors.danger,
              },
              { id: "unverified", label: "Unverified", n: counts.unverified },
              {
                id: "verified",
                label: "Verified",
                n: counts.verified,
                dotColor: statusColors.cool,
              },
              {
                id: "limited",
                label: "Limited",
                n: counts.limited,
                dotColor: statusColors.warn,
              },
              {
                id: "failed",
                label: "Failed",
                n: counts.failed,
                dotColor: statusColors.danger,
              },
            ] as const
          ).map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.id)}
                className={mergeClasses(styles.filterChip, active && styles.filterChipActive)}
                data-testid={`filter-${f.id}`}
              >
                {"dotColor" in f && <span className={styles.filterDot} style={{ backgroundColor: f.dotColor }} />}
                {f.label}
                <span className={styles.filterCount}>{f.n}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.body}>
        {loading && rows.length === 0 ? (
          <div className={styles.loadingWrap} data-testid="hub-list-empty-loader" aria-live="polite">
            <div className={styles.loadingDotsRow} aria-hidden="true">
              <div className={styles.loadingDot} />
              <div className={styles.loadingDot} />
              <div className={styles.loadingDot} />
            </div>
            <span className={styles.loadingLabel}>{loadingMessage}</span>
          </div>
        ) : (
          <>
            {/* ── Streaming UX: shown when resolveEvent is wired up ── */}
            {resolveEvent !== null && resolveEvent !== undefined ? (
              <>
                <StreamingStatusBar stream={stream} />
                <InventoryTimeline entries={stream.eventLog} phase={stream.phase} isLive={stream.isLive} />
              </>
            ) : (
              /* ── Fallback: generic banner when only loading+message ── */
              loading && (
                <output className={styles.loadingBanner} data-testid="hub-list-loading-banner" aria-live="polite">
                  <div className={styles.loadingDotsRow} aria-hidden="true">
                    <div className={styles.loadingDot} />
                    <div className={styles.loadingDot} />
                    <div className={styles.loadingDot} />
                  </div>
                  <span className={styles.loadingBannerLabel}>{loadingMessage}</span>
                </output>
              )
            )}

            {/* ── Live row: pinned at top while a doc is being classified ── */}
            {stream.phase === "classifying" && stream.currentDocName && (
              <LiveDocRow filename={stream.currentDocName} index={stream.currentDocIndex} total={stream.totalDocs} />
            )}

            {rows.length === 0 ? (
              <div className={styles.emptyLibrary} data-testid="hub-list-empty-library">
                <span className={styles.emptyLibraryTitle}>No documents yet</span>
                <span className={styles.emptyLibraryBody}>
                  Upload source documents to this library. Drop files anywhere here, or use the button below.
                </span>
                <button
                  type="button"
                  className={styles.emptyLibraryBtn}
                  onClick={openFilePicker}
                  data-testid="hub-list-empty-upload"
                >
                  Upload documents
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.empty} data-testid="hub-list-empty">
                No matches.
              </div>
            ) : (
              filtered.map((row) => (
                <DocRow
                  key={row.doc_id ?? row.filename}
                  row={row}
                  onClick={onPickDoc}
                  onDeleteDoc={onDeleteDoc}
                  onDownloadDoc={onDownloadDoc}
                />
              ))
            )}
          </>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.addWrap}>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setAddMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={addMenuOpen}
            data-testid="hub-list-add"
          >
            + Add
          </button>
          {addMenuOpen && (
            <>
              <button
                type="button"
                className={styles.addMenuScrim}
                aria-label="Close menu"
                onClick={() => setAddMenuOpen(false)}
              />
              <div className={styles.addMenu} role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className={styles.addMenuItem}
                  onClick={() => {
                    setAddMenuOpen(false);
                    openFilePicker();
                  }}
                  data-testid="hub-list-add-files"
                >
                  Upload a document
                </button>
                {onCreateArtefact && (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.addMenuItem}
                    onClick={() => {
                      setAddMenuOpen(false);
                      onCreateArtefact();
                    }}
                    data-testid="hub-list-create-artefact"
                  >
                    Create artefact
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        {onRefresh && (
          <button type="button" className={styles.refreshBtn} onClick={onRefresh} data-testid="hub-list-refresh">
            ↻ Refresh
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className={styles.hiddenInput}
          onChange={handleFilePickerChange}
        />
        <span className={styles.footerCount}>
          {counts.verified} / {counts.all} verified
        </span>
      </div>
    </div>
  );
};

export default HubList;
